<template>
    <page-controls>
        <template v-slot:left>
            <div class="grow @3xl/controls:max-w-md">
                <vx-filterable :active="active" @toggle="toggleFilterable" data-test="filters">
                    <template v-slot:placeholder>
                        <span class="mr-2">Find</span>
                        <vx-badge
                            v-if="filtersCount > 0"
                            data-test="reset"
                            :borders="false"
                            size="xs"
                            @click.stop="reset"
                        >
                            {{ pluralized('filter', filtersCount, true) }}
                            <font-awesome-icon widthAuto :icon="faCircleXmark" />
                        </vx-badge>
                    </template>
                    <template v-slot:form>
                        <form @submit.prevent="apply" class="flex flex-col gap-4">
                            <div>
                                <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                                    Keyword Search
                                </filter-label>
                                <vx-input
                                    type="text"
                                    data-test="query"
                                    placeholder="Search title or description"
                                    maxlength="80"
                                    size="lg"
                                    v-model="filters.query"
                                >
                                    <template v-slot:right>
                                        <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                                    </template>
                                </vx-input>
                            </div>

                            <div>
                                <filter-label :canReset="!!filters.status?.length" @reset="filters.status = undefined">
                                    <span :id="statusLabelId">Status</span>
                                </filter-label>
                                <div
                                    role="group"
                                    :aria-labelledby="statusLabelId"
                                    class="w-full grid grid-cols-2 gap-1 mb-1"
                                >
                                    <vx-checkbox-selection-item
                                        v-for="option in statusOptions"
                                        :key="option.value"
                                        :active="filters.status?.includes(option.value)"
                                        :data-test="`status-${option.value}`"
                                        class="grow"
                                        @selected="toggleStatus(option.value)"
                                    >
                                        {{ option.label }}
                                    </vx-checkbox-selection-item>
                                </div>
                            </div>

                            <div class="flex gap-2 pt-4">
                                <vx-button
                                    data-test="cancel"
                                    size="lg"
                                    type="button"
                                    color="muted-light"
                                    @click="cancel"
                                >
                                    Cancel
                                </vx-button>
                                <vx-button data-test="apply" size="lg" type="submit" block>
                                    Apply
                                    <font-awesome-icon widthAuto :icon="faCircleCheck" />
                                </vx-button>
                            </div>
                        </form>
                    </template>
                </vx-filterable>
            </div>
            <div class="flex w-full gap-2 @xl/controls:w-auto">
                <sort-selector v-model="sortValue" :options="sortOptions" />
            </div>
        </template>
        <template v-slot:right>
            <slot></slot>
        </template>
    </page-controls>
</template>

<script lang="ts" setup>
import { faCircleCheck, faCircleXmark, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxButton, VxCheckboxSelectionItem, VxFilterable, VxInput } from '@voxie/frontend-components';
import { computed, nextTick, ref, useId, watch } from 'vue';
import PageControls from '~/components/general/PageControls.vue';
import FilterLabel from '~/components/general/FilterLabel.vue';
import SortSelector, { type SortValue } from '~/components/general/SortSelector.vue';
import { pluralized } from '~/components/filters';
import type { TermListParams, TermStatus } from '~/services/adminTermsClient.types';
import { termStatusLabels } from './termStatus';

const props = defineProps<{
    modelValue: TermListParams;
}>();

const emit = defineEmits<{
    'update:modelValue': [value: TermListParams];
    apply: [];
    reset: [];
}>();

const filters = ref<TermListParams>({ ...props.modelValue });

const statusLabelId = useId();
const statusOptions = (['draft', 'published', 'paused', 'archived'] as TermStatus[]).map((status) => ({
    value: status,
    label: termStatusLabels[status],
}));

const toggleStatus = (status: TermStatus) => {
    const current = filters.value.status ?? [];
    const next = current.includes(status) ? current.filter((value) => value !== status) : [...current, status];

    filters.value.status = next.length ? next : undefined;
};

watch(
    () => props.modelValue,
    (modelValue) => {
        filters.value = { ...modelValue };
    },
    { deep: true, immediate: true }
);

const active = ref(false);

const toggleFilterable = (event: boolean) => {
    active.value = event;
};

const filtersCount = computed(() => (props.modelValue.query ? 1 : 0) + (props.modelValue.status?.length ? 1 : 0));

const reset = () => {
    active.value = false;
    emit('reset');
};

const cancel = () => {
    filters.value = { ...props.modelValue };
    active.value = false;
};

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
};

const sortOptions = [{ value: 'id', label: 'Sort by: Created' }];

const sortValue = computed<SortValue>({
    get(): SortValue {
        return {
            field: props.modelValue.sort_field ?? 'id',
            direction: props.modelValue.sort_dir === 'asc' ? 'asc' : 'desc',
        };
    },
    async set(value: SortValue) {
        filters.value.sort_field = value.field;
        filters.value.sort_dir = value.direction;
        await apply();
    },
});
</script>
